import { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { ListFilterIcon } from "lucide-react";

import Layout from "../../components/Layout";
import { PitchFilterSheet } from "@/components/filters/PitchFilterSheet";
import ManagerMonthStats from "@/components/ManagerMonthStats";
import { Heading } from "@/components/typography";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { emptyPitchStatusFilter, pitchMatchesStatusFilter, pitchStatusFilterCount, type PitchStatusFilter } from "../../utils/pitching/filter";
import { canBrowseOpportunities } from "../../utils/pitching/permissions";
import { MyPitchesTable } from "./components/MyPitchesTable";
import { useMyPitches } from "./useMyPitches";

export default function MyPitches() {
    const page = useMyPitches();
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [statusFilter, setStatusFilter] = useState<PitchStatusFilter>(emptyPitchStatusFilter);
    const filterCount = pitchStatusFilterCount(statusFilter);
    const rows = useMemo(
        () => page.rows.filter((row) => pitchMatchesStatusFilter(row, statusFilter)),
        [page.rows, statusFilter],
    );

    if (page.user && !page.isGrailTeam) {
        return <Navigate to={canBrowseOpportunities(page.user) ? "/opportunities" : "/"} replace />;
    }

    return (
        <Layout>
            <ManagerMonthStats
                pitches={page.pitches}
                loading={page.monthLoading}
                actionLabel="View Open Opportunities"
                actionTo="/opportunities"
            />
            <div className="px-4 py-9">
                {/* Title stays left; timeframe and filter stay on the right. */}
                <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
                    <Heading type="h2" tag="h1" className="font-bold leading-none">
                        My Pitches
                    </Heading>
                    {/* Timeframe and Filter share one row. */}
                    <div className="flex flex-wrap items-center gap-4">
                        <span className="text-muted-foreground">Last 3 Months</span>
                        <Button
                            type="button"
                            variant="secondary"
                            className="relative"
                            onClick={() => setFiltersOpen(true)}
                        >
                            <ListFilterIcon data-icon="inline-start" />
                            Filter
                            {filterCount > 0 ? (
                                <Badge variant="blue" className="pointer-events-none absolute -top-2 -right-3">
                                    {filterCount}
                                </Badge>
                            ) : null}
                        </Button>
                    </div>
                </div>
                {page.loading || rows.length > 0 || filterCount > 0 ? (
                    <Card>
                        <CardContent>
                            <MyPitchesTable
                                rows={rows}
                                user={page.user}
                                isLoading={page.loading}
                                empty={(
                                    <div className="px-4 py-16 text-center italic">No pitches match your filters.</div>
                                )}
                            />
                        </CardContent>
                    </Card>
                ) : (
                    <div className="px-4 py-16 text-center">
                        You don&apos;t have any pitches from the last three months.
                    </div>
                )}
            </div>
            <PitchFilterSheet
                open={filtersOpen}
                onOpenChange={setFiltersOpen}
                value={statusFilter}
                onChange={setStatusFilter}
            />
        </Layout>
    );
}
